import { Box, Button } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDeleteDeviceCollection } from '~/api/device-collections/delete';
import { ConfirmDialog } from '~/components/dialogs/confirmation';
import { useConfirmDialog } from '~/hooks/dialogs';
import { useDeviceFilters } from '../context';
import { SavedFilterAutocomplete } from './SavedFilterAutocomplete/SavedFilterAutocomplete';
import type { SavedFilters_DeviceDynamicCollection as SavedFilter } from './SavedFilterAutocomplete/SavedFilterAutocomplete.generated';

export const SavedFilterSelector = () => {
  const { t } = useTranslation();
  const { reinitialize } = useDeviceFilters();

  const [selectedFilter, setSelectedFilter] = useState<SavedFilter | null>(null);

  const [confirm, confirmationProps] = useConfirmDialog();
  const [deleteFilter] = useDeleteDeviceCollection();

  const onDeleteFilter = async (filter: SavedFilter) => {
    if (!(await confirm())) return;
    await deleteFilter({
      variables: { input: { id: filter.id }, source: 'saved_filter' },
    });
    setSelectedFilter(null);
  };

  const onSelectFilter = (filter: SavedFilter | null) => {
    setSelectedFilter(filter);
  };

  const onApplyFilter = () => {
    if (!selectedFilter) return;
    reinitialize(selectedFilter.query);
    setSelectedFilter(null);
  };

  return (
    <>
      <Box display="flex" flexDirection="row" gap={1} paddingX={2} paddingBottom={1}>
        <SavedFilterAutocomplete
          onChange={onSelectFilter}
          onDelete={onDeleteFilter}
          value={selectedFilter}
        />
        <Button
          disabled={!selectedFilter}
          variant="contained"
          color="primary"
          onClick={onApplyFilter}
          size="small"
        >
          {t('common.buttons.apply')}
        </Button>
      </Box>
      <ConfirmDialog
        {...confirmationProps}
        confirm={t('pages.devices.filters.delete_filter_confirm')}
        deleteConfirm
        prompt={t('pages.devices.filters.delete_filter_prompt')}
        title={t('pages.devices.filters.delete_filter')}
      />
    </>
  );
};
